import { useMemo } from "react"
import { Empty } from "antd"
import { useParams } from "react-router-dom"
import { byDomain, defaultSplitFor, groupTableMetrics, type MetricDefinition } from "@csbi/metric-catalog"
import { useTheme } from "../../app/providers/theme-provider.js"
import { Tile } from "../../features/tiles/components/tile.js"
import { ExportMenu } from "../../features/tiles/components/export-menu.js"
import type { TileSpec } from "../../features/tiles/hooks/use-tile-query.js"
import { PageHeader, SectionTitle } from "../../shared/components/page-header.js"
import { DOMAIN_BY_SLUG } from "../../shared/config/site.js"

const THREE_UP = "repeat(auto-fit, minmax(max(260px, (100% - 24px) / 3), 1fr))"

const isKpi = (m: MetricDefinition) => m.defaultWidget === "stat-tile"
const isWide = (m: MetricDefinition) => m.defaultWidget === "table" || m.defaultWidget === "map"

export function DomainPage() {
  const { domainSlug = "" } = useParams()
  const { mode } = useTheme()
  const nav = DOMAIN_BY_SLUG.get(domainSlug)

  const metrics = useMemo(() => (nav ? (byDomain.get(nav.domain) ?? []) : []), [nav])

  /**
   * ⚠ **The specs are built once and used by both the tiles and the export.**
   */
  const domainLabel = nav?.label ?? ""

  const groups = useMemo(() => {
    const tile = (m: MetricDefinition, splitBy: TileSpec["splitBy"]) => ({
      title: m.name,
      spec: { tileKey: `domain.${m.id}`, metrics: [m.id], splitBy } satisfies TileSpec,
    })

    /**
     * ⚠ **Table metrics are grouped before they become tiles — several of them can share one table.**
     */
    const tables = groupTableMetrics(metrics).map((g) => ({
      title: g.metrics.length > 1 ? domainLabel : (g.metrics[0]?.name ?? domainLabel),
      spec: {
        tileKey: `domain.table.${g.metrics[0]?.id ?? "none"}`,
        metrics: g.metrics.map((m) => m.id),
        splitBy: [...g.dims],
      } satisfies TileSpec,
    }))

    const maps = metrics.filter((m) => m.defaultWidget === "map").map((m) => tile(m, defaultSplitFor(m)))

    return {
      kpis: metrics.filter(isKpi).map((m) => tile(m, ["period"])),
      charts: metrics.filter((m) => !isKpi(m) && !isWide(m)).map((m) => tile(m, defaultSplitFor(m))),
      wide: [...tables, ...maps],
    }
  }, [metrics, domainLabel])

  if (!nav) return <Empty description={`No such metric domain: ${domainSlug}`} />

  const { kpis, charts, wide } = groups

  return (
    <>
      <PageHeader
        title={nav.label}
        extra={
          <ExportMenu
            fileBase={nav.label}
            groups={[
              { label: "Key figures", tiles: kpis },
              { label: "Charts", tiles: charts },
              { label: "Detail", tiles: wide },
            ]}
          />
        }
      />

      {kpis.length > 0 && (
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: THREE_UP }}>
          {kpis.map((t) => (
            <div key={t.spec.tileKey} style={{ minHeight: 132 }}>
              <Tile spec={t.spec} widget="stat-tile" mode={mode} />
            </div>
          ))}
        </div>
      )}

      {charts.length > 0 && (
        <>
          <SectionTitle>Charts</SectionTitle>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: THREE_UP }}>
            {charts.map((t) => (
              <div key={t.spec.tileKey} style={{ height: 300 }}>
                <Tile spec={t.spec} mode={mode} />
              </div>
            ))}
          </div>
        </>
      )}

      {wide.length > 0 && (
        <>
          <SectionTitle>Detail</SectionTitle>
          <div style={{ display: "grid", gap: 12 }}>
            {wide.map((t) => (
              <div key={t.spec.tileKey} style={{ height: 320 }}>
                {/* Named explicitly: a grouped table would otherwise be titled by joining its metric names with " · " */}
                <Tile spec={t.spec} title={t.title} mode={mode} />
              </div>
            ))}
          </div>
        </>
      )}

      {metrics.length === 0 && <Empty description="No metrics in this domain" />}
    </>
  )
}
